<template>
    <div class="flex flex-col gap-6 p-6 md:gap-4 md:p-0">
        <div v-if="!isMd && currentOrgUnit?.id" class="w-full">
            <router-button
                data-test="back-to-threads"
                color="muted-dark"
                size="md"
                class="w-full"
                :to="{ name: 'message-hub', query: route.query }"
            >
                <font-awesome-icon widthAuto :icon="faArrowLeft" />
                Back to threads
            </router-button>
        </div>

        <vx-alert v-if="success" shadow color="success" data-test="success-alert" :closeable="false">
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faCircleCheck" />
            </template>

            {{ success }}
            <template v-slot:right>
                <vx-button
                    v-if="undoAction"
                    @click="executeUndo"
                    size="sm"
                    color="success"
                    :loading="undoing"
                    data-test="undo-button"
                >
                    Undo
                    <font-awesome-icon widthAuto :icon="faUndo" />
                </vx-button>
            </template>
        </vx-alert>

        <!-- Error Alert -->
        <vx-alert v-if="error" shadow color="danger" data-test="error-alert" :closeable="false">
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faTriangleExclamation" class="text-rose-500"></font-awesome-icon>
            </template>
            {{ error }}
            <template v-slot:right>
                <vx-button size="sm" color="danger" @click="error = ''">
                    Close
                    <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                </vx-button>
            </template>
        </vx-alert>

        <div v-if="currentOrgUnit?.id" class="max-h-full overflow-auto scroll-thin">
            <message-hub-bulk-actions-by-groups />
        </div>

        <vx-box v-else color="info">
            <div
                class="flex items-center justify-between px-6 py-4 border-b border-x-0 border-t-0 border-solid border-slate-200"
            >
                <div class="flex items-center gap-3">
                    <span class="text-xl">
                        <span class="font-bold">
                            {{ pluralized('thread', selectedThreads.size, true) }}
                        </span>
                        selected
                    </span>
                    <div>
                        <vx-button @click="clearSelection()" data-test="clear-button" size="xs" color="muted">
                            Clear
                        </vx-button>
                    </div>
                </div>
                <div class="flex justify-end gap-2">
                    <vx-button
                        @click="bulkUnsubscribeContacts(Array.from(selectedThreads.values()))"
                        :loading="unsubscribingContacts"
                        outline
                        color="muted"
                        size="md"
                        data-test="unsubscribe-button"
                    >
                        Unsubscribe
                        <font-awesome-icon widthAuto :icon="faArrowRightFromBracket" />
                    </vx-button>
                </div>
            </div>

            <div class="flex flex-col gap-4 p-6">
                <message-hub-bulk-actions-bar></message-hub-bulk-actions-bar>

                <message-builder
                    v-model="message.body"
                    v-model:mediaUrl="message.media_url"
                    v-model:mediaContentType="message.media_content_type"
                    :disabled="sendingMessage"
                    placeholder="Message multiple contacts..."
                    @submit="handleSend"
                    @schedule="handleSchedule"
                    :blockImages="blockImages"
                    :blockFiles="blockFiles"
                    maxHeight
                />
            </div>
        </vx-box>
    </div>
</template>

<script setup>
import {
    faArrowLeft,
    faArrowRightFromBracket,
    faCircleCheck,
    faXmark,
    faTriangleExclamation,
    faUndo,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useRoute } from 'vue-router';
import { watch } from 'vue';
import { VxAlert, VxBox, VxButton } from '@voxie/frontend-components';
import { pluralized } from '~/components/filters';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useMessageHubRules } from '~/composables/useMessageHubRules';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { useMessageHub } from '../useMessageHub';
import MessageHubBulkActionsBar from './MessageHubBulkActionsBar.vue';
import MessageHubBulkActionsByGroups from './MessageHubBulkActionsByGroups.vue';
import { useMessageHubBulkActions } from './useMessageHubBulkActions';
import { useToasted } from '~/composables/useToasted';
import RouterButton from '~/components/general/RouterButton.vue';

const route = useRoute();
const { isMd } = useResponsiveLayout();
const { currentOrgUnit, currentGroup } = useCurrentGroup();
const { blockImages, blockFiles, getMessageHubRules } = useMessageHubRules();

watch(
    () => currentGroup.value?.group_id,
    (groupId) => {
        getMessageHubRules(groupId);
    },
    { immediate: true }
);
const { selectedThreads, clearSelection } = useMessageHub();
const toasted = useToasted();

const {
    bulkUnsubscribeContacts,
    sendBulkMessage,
    scheduleBulkMessage,
    resetMessageData,
    message,
    sendingMessage,
    unsubscribingContacts,
    success,
    error,
    executeUndo,
    undoAction,
    undoing,
} = useMessageHubBulkActions();

const handleSend = async () => {
    if (!message.value.body?.trim()) {
        toasted.error('Message body is required.');
        return;
    }

    await sendBulkMessage(Array.from(selectedThreads.value.values()), message.value);
    resetMessageData();
};

const handleSchedule = async (scheduleAt) => {
    await scheduleBulkMessage(scheduleAt, Array.from(selectedThreads.value.values()), message.value);
    resetMessageData();
};
</script>
